import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiSend,
  FiCheck,
  FiArrowRight,
  FiZap,
  FiNavigation,
  FiUser,
  FiHelpCircle,
  FiCpu,
  FiPlus,
  FiSidebar,
  FiArrowLeft,
  FiMessageSquare,
  FiMic,
  FiSquare,
  FiTrash2,
} from 'react-icons/fi';
import { useSector } from '../../contexts/SectorContext';
import {
  isMendsVisionEnabled,
  mindChat,
  createApproval,
  approveItem,
  undoApproval,
  editApproval,
  generateBriefing,
  registerConversation,
  transcribeAudio,
  listChatSessions,
  saveChatSession,
  deleteChatSession,
  MindChatAction,
  ChatSessionDto,
} from '../services/MendsVisionService';
import { openRegisterConversation } from '../openRegister';
import { bestHelpAnswer } from '../knowledge/systemHelp';
import {
  matchActions,
  extractContactHint,
  AiActionDef,
  kindLabel,
  actionKindFromPath,
  actionPayloadFromPath,
} from '../lib/aiActions';
import { Markdown } from './Markdown';
import { sanitizeMindReply } from '../lib/sanitizeMindReply';
import { readActiveContact } from '../lib/activeContact';
import { describeCreateOutcome } from '../lib/createApprovalOutcome';
import {
  DraftCardState,
  discardOpenDrafts,
  lastOpenDraftState,
  newDraftState,
  openDraftFromMessages,
  restoreDraftState,
} from '../lib/mindDraft';
import { useMindDrafts } from '../lib/useMindDrafts';
import { MindDraftCard } from './MindDraftCard';
import '../styles/mendsAi.css';

type ActionCard = {
  id: string;
  kind: string;
  title: string;
  subtitle?: string | null;
  path: string;
  contactId?: number | null;
  requiresConfirm: boolean;
  approvalTitle?: string | null;
  approvalBody?: string | null;
  suggestedAction?: string | null;
  actionKind?: string | null;
  actionPayload?: string | null;
  status?: 'idle' | 'submitting' | 'queued' | 'executing' | 'done' | 'error' | 'done-nav' | 'undone';
  message?: string;
  approvalId?: number;
  editBody?: string;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  source?: string;
  actions?: ActionCard[];
  /** Card de rascunho do chat (ação `draft` do backend); salvo na sessão. */
  draft?: DraftCardState;
};

const SUGGESTIONS = [
  'O que preciso fazer hoje?',
  'Como estão meus leads?',
  'Gerar briefing comercial',
  'Como funcionam as Aprovações?',
  'Ir para Conversas',
];

const MAX_VOICE_MS = 90_000;
const SESSION_TRIM = 40;

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function actionFromDef(a: AiActionDef): ActionCard {
  return {
    id: a.id,
    kind: a.kind,
    title: a.title,
    subtitle: a.subtitle,
    path: a.path,
    requiresConfirm: a.requiresConfirm,
    approvalTitle: a.approvalTemplate?.title,
    approvalBody: a.approvalTemplate?.body,
    suggestedAction: a.approvalTemplate?.suggestedAction,
    actionKind: a.approvalTemplate?.actionKind ?? actionKindFromPath(a.path),
    actionPayload: a.approvalTemplate?.actionPayload,
    status: 'idle',
  };
}

function actionFromApi(a: MindChatAction, i: number): ActionCard {
  return {
    id: `api-${a.path}-${i}-${uid()}`,
    kind: a.kind,
    title: a.title,
    subtitle: a.subtitle,
    path: a.path,
    contactId: a.contactId,
    requiresConfirm: a.requiresConfirm || a.kind === 'mutation',
    approvalTitle: a.approvalTitle,
    approvalBody: a.approvalBody,
    suggestedAction: a.suggestedAction,
    actionKind: a.actionKind ?? actionKindFromPath(a.path),
    actionPayload: a.actionPayload,
    status: 'idle',
  };
}

function TypeIcon({ kind }: { kind: string }) {
  if (kind === 'help') return <FiHelpCircle size={14} />;
  if (kind === 'contact') return <FiUser size={14} />;
  if (kind === 'mutation') return <FiZap size={14} />;
  if (kind === 'nav') return <FiNavigation size={14} />;
  return <FiArrowRight size={14} />;
}

function resolveLocal(message: string): { reply: string; actions: ActionCard[]; strong: boolean } | null {
  const q = message.trim();
  if (!q) return null;

  const help = bestHelpAnswer(q);
  const actions = matchActions(q, 4).map(actionFromDef);
  const contactHint = extractContactHint(q);

  const strongAction = actions.length > 0 && (
    actions[0].kind === 'mutation'
    || actions[0].kind === 'action'
    || /\b(go to|open|show|take me|ir para|abrir|mostrar)\b/i.test(q)
  );

  if (help) {
    const helpAction: ActionCard | null = help.relatedPath
      ? {
          id: `help-${help.id}`,
          kind: help.relatedPath.startsWith('action:') ? 'action' : 'nav',
          title: help.relatedLabel || help.title,
          subtitle: help.title,
          path: help.relatedPath,
          requiresConfirm: false,
          status: 'idle',
        }
      : null;
    const merged = [
      ...(helpAction ? [helpAction] : []),
      ...actions.filter((a) => a.path !== helpAction?.path),
    ].slice(0, 4);
    return { reply: help.answer, actions: merged, strong: true };
  }

  if (strongAction && !contactHint) {
    const top = actions[0];
    const reply =
      top.kind === 'mutation'
        ? `I can propose “${top.title}”. Confirm below — Ok will execute when possible.`
        : top.kind === 'nav'
          ? `I can take you to ${top.title.replace(/^Go to\s+/i, '')}.`
          : `I can run: ${top.title}.`;
    return { reply, actions: actions.slice(0, 3), strong: true };
  }

  return null;
}

function greetingLine() {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

function titleFromMessages(msgs: ChatMessage[]) {
  const first = msgs.find((m) => m.role === 'user');
  return first ? first.text.slice(0, 48) : 'New chat';
}

export const AiMindChat: React.FC = () => {
  const { selectedSectorId } = useSector();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sessions, setSessions] = useState<ChatSessionDto[]>([]);
  const [recording, setRecording] = useState(false);
  const [voiceHint, setVoiceHint] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordStartedAt = useRef(0);
  const stopTimerRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string | undefined>(undefined);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const conversationGenRef = useRef(0);
  const [persistTick, setPersistTick] = useState(0);
  const requestPersist = useCallback(() => setPersistTick((t) => t + 1), []);

  const loadSessions = useCallback(async () => {
    if (!selectedSectorId) {
      setSessions([]);
      return;
    }
    try {
      const list = await listChatSessions(Number(selectedSectorId));
      setSessions(list || []);
    } catch {
      // local fallback: ignore
    }
  }, [selectedSectorId]);

  useEffect(() => { void loadSessions(); }, [loadSessions]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onFocus = () => inputRef.current?.focus();
    const onPrompt = (e: Event) => {
      const prompt = (e as CustomEvent).detail?.prompt as string | undefined;
      if (prompt) void send(prompt);
    };
    const onVoice = () => { void startRecording(); };
    window.addEventListener('mends-mind-focus', onFocus);
    window.addEventListener('mends-mind-prompt', onPrompt);
    window.addEventListener('mends-mind-voice', onVoice);
    return () => {
      window.removeEventListener('mends-mind-focus', onFocus);
      window.removeEventListener('mends-mind-prompt', onPrompt);
      window.removeEventListener('mends-mind-voice', onVoice);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectorId, busy]);

  // Deep link ?q= / ?voice=1
  useEffect(() => {
    const q = searchParams.get('q');
    const voice = searchParams.get('voice');
    if (q) {
      setSearchParams({}, { replace: true });
      void send(q);
    } else if (voice === '1') {
      setSearchParams({}, { replace: true });
      void startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  // Saves run one after another so a fast second save reuses the id of the first (never two sessions,
  // never an older card status overwriting a newer one).
  const persistSession = useCallback((msgs: ChatMessage[], id?: string) => {
    if (!selectedSectorId || msgs.length === 0) return saveChainRef.current;
    const trimmed = msgs.slice(-SESSION_TRIM);
    const sectorId = Number(selectedSectorId);
    const gen = conversationGenRef.current;
    const title = titleFromMessages(trimmed);
    const messagesJson = JSON.stringify(
      trimmed.map(({ id: mid, role, text, source, draft }) => ({ id: mid, role, text, source, draft })),
    );
    saveChainRef.current = saveChainRef.current.then(async () => {
      if (gen !== conversationGenRef.current) return;
      try {
        const saved = await saveChatSession({ sectorId, id: sessionIdRef.current ?? id, title, messagesJson });
        if (gen !== conversationGenRef.current) return;
        sessionIdRef.current = saved.id;
        setSessionId(saved.id);
        void loadSessions();
      } catch {
        // ignore — chat still works offline for the tab
      }
    });
    return saveChainRef.current;
  }, [selectedSectorId, loadSessions]);

  useEffect(() => {
    if (persistTick === 0) return;
    void persistSession(messages, sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistTick]);

  const drafts = useMindDrafts<ChatMessage>({
    sectorId: selectedSectorId ? Number(selectedSectorId) : null,
    messages,
    setMessages,
    requestPersist,
  });

  const resetConversation = useCallback(() => {
    conversationGenRef.current += 1;
    sessionIdRef.current = undefined;
    setMessages([]);
    setInput('');
    setBusy(false);
    setSessionId(undefined);
    setVoiceHint(null);
    setTimeout(() => inputRef.current?.focus(), 40);
  }, []);

  const openSession = useCallback((s: ChatSessionDto) => {
    try {
      const parsed = JSON.parse(s.messagesJson || '[]') as ChatMessage[];
      conversationGenRef.current += 1;
      sessionIdRef.current = s.id;
      setMessages(Array.isArray(parsed)
        ? parsed.map((m) => (m.draft ? { ...m, draft: restoreDraftState(m.draft) } : m))
        : []);
      setSessionId(s.id);
    } catch {
      setMessages([]);
    }
  }, []);

  const removeSession = useCallback(async (id: string) => {
    if (!selectedSectorId) return;
    try {
      await deleteChatSession(Number(selectedSectorId), id);
      if (sessionId === id) resetConversation();
      void loadSessions();
    } catch {
      /* ignore */
    }
  }, [selectedSectorId, sessionId, resetConversation, loadSessions]);

  const updateAction = useCallback((msgId: string, actionId: string, patch: Partial<ActionCard>) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msgId || !m.actions) return m;
        return {
          ...m,
          actions: m.actions.map((a) => (a.id === actionId ? { ...a, ...patch } : a)),
        };
      }),
    );
  }, []);

  const runNavOrAction = useCallback((card: ActionCard) => {
    if (card.path === 'action:register-conversation' || card.path === 'action:register-voice') {
      openRegisterConversation();
      return;
    }
    if (card.path.startsWith('/')) {
      navigate(card.path);
    }
  }, [navigate]);

  const queueApproval = useCallback(async (msgId: string, card: ActionCard, originalQuery: string) => {
    if (!selectedSectorId) {
      updateAction(msgId, card.id, {
        status: 'error',
        message: 'Select a connection in the menu first.',
      });
      return;
    }
    const title = card.approvalTitle || card.title;
    const body =
      (card.editBody ?? card.approvalBody ?? card.subtitle ?? card.title) +
      (originalQuery ? `\n\nOriginal request: “${originalQuery}”` : '');
    updateAction(msgId, card.id, { status: 'submitting' });
    try {
      let actionPayload = card.actionPayload || actionPayloadFromPath(card.path) || undefined;
      if (card.actionKind === 'send_message' && card.editBody) {
        try {
          const prev = actionPayload ? JSON.parse(actionPayload) : {};
          const draftMatch = card.editBody.match(/(?:Draft|Message)\s*:\s*(.+)$/is);
          actionPayload = JSON.stringify({
            ...prev,
            contactId: card.contactId ?? prev.contactId,
            text: draftMatch?.[1]?.trim() || prev.text || card.editBody,
          });
        } catch { /* keep */ }
      }
      const created = await createApproval({
        sectorId: Number(selectedSectorId),
        contactId: card.contactId ?? null,
        contactName: card.kind === 'contact' ? card.title : null,
        title,
        body,
        suggestedAction: card.suggestedAction || 'Review and confirm',
        actionKind: card.actionKind || actionKindFromPath(card.path),
        actionPayload,
      });
      const outcome = describeCreateOutcome(created);
      if (outcome.kind !== 'queued') {
        updateAction(msgId, card.id, {
          status: outcome.kind === 'executed' ? 'done' : 'error',
          message: outcome.message,
        });
        return;
      }
      updateAction(msgId, card.id, {
        status: 'queued',
        message: 'Na fila — abra Aprovações e clique Ok para executar.',
        approvalId: created.id,
        editBody: body,
      });
    } catch (e: any) {
      updateAction(msgId, card.id, {
        status: 'error',
        message: e?.response?.data?.message || e?.message || 'Failed to queue.',
      });
    }
  }, [selectedSectorId, updateAction]);

  const executeOk = useCallback(async (msgId: string, card: ActionCard) => {
    if (!selectedSectorId || !card.approvalId) {
      // Queue first if needed
      await queueApproval(msgId, card, '');
      return;
    }
    updateAction(msgId, card.id, { status: 'executing' });
    try {
      // Persist edits before execute
      if (card.editBody) {
        await editApproval(Number(selectedSectorId), card.approvalId, card.editBody, undefined, card.actionPayload || undefined);
      }
      const result = await approveItem(Number(selectedSectorId), card.approvalId);
      if (result.executionError) {
        updateAction(msgId, card.id, {
          status: 'error',
          message: result.executionError,
        });
        return;
      }
      const msg = result.executionResult
        || (result.executed ? 'Done.' : 'Approved.');
      updateAction(msgId, card.id, {
        status: 'done',
        message: msg,
      });
      // If briefing text came back, append as assistant message
      if (result.actionKind === 'generate_briefing' || card.actionKind === 'generate_briefing' || card.path === 'mutation:briefing') {
        if (result.executionResult && result.executionResult.length > 40) {
          setMessages((prev) => [
            ...prev,
            { id: uid(), role: 'assistant', text: result.executionResult!, source: 'briefing' },
          ]);
        } else if (selectedSectorId) {
          try {
            const b = await generateBriefing(Number(selectedSectorId), true);
            setMessages((prev) => [
              ...prev,
              { id: uid(), role: 'assistant', text: b.text, source: b.usedLlm ? 'llm' : 'local' },
            ]);
          } catch { /* ignore */ }
        }
      }
      if (result.navigatePath && (card.actionKind === 'open_chat' || card.actionKind === 'navigate')) {
        navigate(result.navigatePath);
      }
    } catch (e: any) {
      updateAction(msgId, card.id, {
        status: 'error',
        message: e?.response?.data?.message || e?.message || 'Execution failed.',
      });
    }
  }, [selectedSectorId, updateAction, queueApproval, navigate]);

  const handleActionClick = useCallback((msgId: string, card: ActionCard) => {
    if (card.requiresConfirm || card.kind === 'mutation' || card.path.startsWith('mutation:')) {
      return;
    }
    updateAction(msgId, card.id, { status: 'done-nav' });
    runNavOrAction(card);
  }, [runNavOrAction, updateAction]);

  const send = useCallback(async (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;

    const userMsg: ChatMessage = { id: uid(), role: 'user', text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setBusy(true);
    setVoiceHint(null);

    try {
      // Voice / register transcript → organize
      if (/^\[voice transcript\]/i.test(text) || /^\[registered conversation\]/i.test(text)) {
        const clean = text.replace(/^\[(voice transcript|registered conversation)\]\s*/i, '');
        if (selectedSectorId) {
          try {
            const reg = await registerConversation({
              sectorId: Number(selectedSectorId),
              text: clean,
            });
            const reply = [
              '**Registered conversation**',
              '',
              reg.organizedNotes,
              '',
              reg.proposedUpdates?.length
                ? `### Proposed updates\n${reg.proposedUpdates.map((u) => `- ${u}`).join('\n')}`
                : '',
            ].filter(Boolean).join('\n');
            const assistant: ChatMessage = {
              id: uid(),
              role: 'assistant',
              text: reply,
              source: reg.usedLlm ? 'llm' : 'local',
              actions: reg.contactId
                ? [{
                    id: uid(),
                    kind: 'nav',
                    title: `Open ${reg.contactName}`,
                    path: `/chat?contactId=${reg.contactId}`,
                    contactId: reg.contactId,
                    requiresConfirm: false,
                    status: 'idle',
                  }]
                : undefined,
            };
            // Functional updates everywhere in send(): a card that changed while we waited (e.g. sent)
            // must never be put back to the snapshot taken when the seller hit Send.
            setMessages((prev) => [...prev, assistant]);
            requestPersist();
            return;
          } catch {
            // fall through to normal chat
          }
        }
      }

      // With a sector, every message goes to the backend first (it decides what is a draft request);
      // the local shortcut only answers without a sector or when the call fails (catch below).
      if (!selectedSectorId) {
        const local = resolveLocal(text);
        if (local?.strong) {
          const assistant: ChatMessage = {
            id: uid(),
            role: 'assistant',
            text: local.reply,
            source: 'local',
            actions: local.actions,
          };
          setMessages((prev) => [...prev, assistant]);
          return;
        }
        const assistant: ChatMessage = {
          id: uid(),
          role: 'assistant',
          text: local?.reply
            || 'Select a connection in the menu so I can look up contacts and sector context. Meanwhile I can help with navigation and product questions.',
          source: 'local',
          actions: local?.actions?.length ? local.actions : matchActions(text, 3).map(actionFromDef),
        };
        setMessages((prev) => [...prev, assistant]);
        return;
      }

      const history = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.text.slice(0, 400) }));

      const active = readActiveContact();
      const sectorId = Number(selectedSectorId);
      const previousOpen = lastOpenDraftState(messages);
      const res = await mindChat({
        sectorId,
        message: text,
        history,
        contactId: active && active.sectorId === sectorId ? active.contactId : undefined,
        openDraft: openDraftFromMessages(messages),
      });

      const cleaned = sanitizeMindReply(res.reply || '', res.actions || []);
      const draftAction = cleaned.actions.find((a) => a.kind === 'draft' && a.draft);
      const apiActions = cleaned.actions.filter((a) => a.kind !== 'draft').map(actionFromApi);
      const actions = draftAction || apiActions.length > 0
        ? apiActions
        : matchActions(text, 3).map(actionFromDef);

      const assistant: ChatMessage = {
        id: uid(),
        role: 'assistant',
        text: cleaned.reply || 'Não entendi por completo — reformule ou escolha uma sugestão.',
        source: res.source || (res.usedLlm ? 'llm' : 'api'),
        actions,
        draft: draftAction?.draft ? newDraftState(draftAction.draft, previousOpen) : undefined,
      };
      // Functional update: a card action that finished while we waited is not overwritten.
      setMessages((prev) => [...(assistant.draft ? discardOpenDrafts(prev) : prev), assistant]);
      requestPersist();
    } catch {
      const fallback = resolveLocal(text) || {
        reply: 'Could not reach the server. Use the suggestions or try again.',
        actions: matchActions(text, 3).map(actionFromDef),
      };
      const assistant: ChatMessage = {
        id: uid(),
        role: 'assistant',
        text: fallback.reply,
        source: 'local-fallback',
        actions: fallback.actions,
      };
      setMessages((prev) => [...prev, assistant]);
    } finally {
      setBusy(false);
    }
  }, [busy, messages, selectedSectorId, requestPersist]);

  const startRecording = useCallback(async () => {
    if (recording || busy) return;
    if (!selectedSectorId) {
      setVoiceHint('Select a connection before recording.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined;
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recordStartedAt.current = Date.now();
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (stopTimerRef.current) {
          window.clearTimeout(stopTimerRef.current);
          stopTimerRef.current = null;
        }
        const durationMs = Date.now() - recordStartedAt.current;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setRecording(false);
        if (blob.size < 200) {
          setVoiceHint('Recording too short.');
          return;
        }
        setBusy(true);
        setVoiceHint('Transcribing…');
        try {
          const result = await transcribeAudio(
            Number(selectedSectorId),
            blob,
            'voice.webm',
            durationMs,
          );
          setVoiceHint(null);
          setBusy(false);
          await send(`[voice transcript]\n${result.text}`);
        } catch (e: any) {
          setBusy(false);
          setVoiceHint(e?.response?.data?.message || e?.message || 'Transcription failed.');
        }
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
      setVoiceHint('Listening… tap stop when done (max 90s).');
      stopTimerRef.current = window.setTimeout(() => {
        if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
      }, MAX_VOICE_MS);
    } catch {
      setVoiceHint('Microphone permission denied.');
    }
  }, [recording, busy, selectedSectorId, send]);

  const stopRecording = useCallback(() => {
    if (mediaRef.current?.state === 'recording') {
      mediaRef.current.stop();
    }
  }, []);

  const onComposerKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  if (!isMendsVisionEnabled()) return null;

  const lastUserText = [...messages].reverse().find((m) => m.role === 'user')?.text || '';
  const isEmpty = messages.length === 0;

  return (
    <div className="mai-mind-page">
      <aside className={`mai-mind-rail ${railOpen ? '' : 'mai-mind-rail-collapsed'}`}>
        <div className="mai-mind-rail-top">
          <button type="button" className="mai-mind-new" onClick={resetConversation}>
            <FiPlus size={16} />
            <span>New chat</span>
          </button>
          <button
            type="button"
            className="mai-mind-rail-toggle"
            onClick={() => setRailOpen((v) => !v)}
            aria-label={railOpen ? 'Collapse panel' : 'Expand panel'}
          >
            <FiSidebar size={16} />
          </button>
        </div>
        {railOpen && (
          <>
            <div className="mai-mind-rail-section">
              <div className="mai-mind-rail-label">Suggestions</div>
              {SUGGESTIONS.slice(0, 4).map((s) => (
                <button
                  key={s}
                  type="button"
                  className="mai-mind-rail-chip"
                  disabled={busy}
                  onClick={() => void send(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            {sessions.length > 0 && (
              <div className="mai-mind-rail-section">
                <div className="mai-mind-rail-label">History</div>
                {sessions.slice(0, 8).map((s) => (
                  <div key={s.id} className="mai-mind-session-row">
                    <button
                      type="button"
                      className={`mai-mind-rail-chip ${sessionId === s.id ? 'mai-mind-rail-chip-active' : ''}`}
                      onClick={() => openSession(s)}
                    >
                      {s.title || 'Chat'}
                    </button>
                    <button
                      type="button"
                      className="mai-mind-session-del"
                      title="Delete"
                      onClick={() => void removeSession(s.id)}
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mai-mind-rail-footer">
              <button type="button" className="mai-mind-rail-link mai-mind-rail-link-primary" onClick={() => navigate('/chat')}>
                <FiMessageSquare size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                Conversas
              </button>
              <button type="button" className="mai-mind-rail-link" onClick={() => navigate('/central-inteligencia?tab=aprovacoes')}>
                Aprovações
              </button>
              <button type="button" className="mai-mind-rail-link" onClick={() => navigate('/central-inteligencia')}>
                Central / Hoje
              </button>
            </div>
          </>
        )}
      </aside>

      <div className="mai-mind-main">
        <header className="mai-mind-page-header">
          <div className="mai-mind-brand">
            <button type="button" className="mai-mind-back" onClick={() => navigate('/chat')} title="Voltar para as conversas">
              <FiArrowLeft size={16} />
              <span>Conversas</span>
            </button>
            <span className="mai-mind-orb" aria-hidden />
            <div>
              <div className="mai-mind-title">Mends AI</div>
              <div className="mai-mind-sub">Inside the CRM · confirm before send</div>
            </div>
          </div>
          <div className="mai-mind-header-actions">
            <button type="button" className="mai-mind-chip-btn" onClick={resetConversation}>
              New chat
            </button>
            <span className="mai-cmd-kbd">⌘K</span>
          </div>
        </header>

        <div className="mai-mind-scroll" ref={scrollerRef}>
          <div className="mai-mind-column">
            {isEmpty && (
              <div className="mai-mind-empty">
                <span className="mai-mind-orb mai-mind-orb-lg" aria-hidden />
                <h1 className="mai-mind-empty-title">{greetingLine()}.</h1>
                <p className="mai-mind-empty-sub">
                  Pergunte sobre leads, abra uma tela, rascunhe uma mensagem ou registre uma conversa.
                  Ações sensíveis vão para a fila — Ok executa.
                </p>
                <div className="mai-mind-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" className="mai-mind-suggestion" onClick={() => void send(s)} disabled={busy}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`mai-mind-row mai-mind-row-${m.role}`}>
                {m.role === 'assistant' && (
                  <div className="mai-mind-avatar" aria-hidden>
                    <FiCpu size={14} />
                  </div>
                )}
                <div className={`mai-mind-bubble mai-mind-bubble-${m.role}`}>
                  {m.role === 'assistant' ? <Markdown text={m.text} /> : <p>{m.text}</p>}
                  {m.source && m.role === 'assistant' && (
                    <span className="mai-mind-source">
                      {m.source === 'local' || m.source === 'local-fallback'
                        ? 'local knowledge'
                        : m.source === 'llm' || m.source === 'briefing'
                          ? 'AI'
                          : m.source}
                    </span>
                  )}
                  {m.draft && (
                    m.draft.status === 'discarded' && m.draft.message ? (
                      <div className="mai-draft-discarded">{m.draft.message}</div>
                    ) : (
                      <MindDraftCard
                        draft={m.draft.draft}
                        status={m.draft.status}
                        message={m.draft.message}
                        onTextChange={(t) => drafts.changeText(m.id, t)}
                        onEdit={() => drafts.toggleEdit(m.id)}
                        onQueue={() => void drafts.queue(m.id)}
                        onSendNow={() => void drafts.sendNow(m.id)}
                        onDiscard={() => drafts.discard(m.id)}
                        includePdf={m.draft.includePdf !== false}
                        onTogglePdf={() => drafts.togglePdf(m.id)}
                      />
                    )
                  )}
                  {m.actions && m.actions.length > 0 && (
                    <div className="mai-mind-actions">
                      {m.actions.map((card) => (
                        <div
                          key={card.id}
                          className={`mai-mind-action-card ${card.requiresConfirm || card.kind === 'mutation' ? 'mai-mind-action-mutate' : ''}`}
                        >
                          <div className="mai-mind-action-top">
                            <span className={`mai-cmd-type mai-cmd-type-${card.kind}`}>
                              <TypeIcon kind={card.kind} />
                              {kindLabel(card.kind)}
                            </span>
                            {(card.requiresConfirm || card.kind === 'mutation') && (
                              <span className="mai-cmd-badge-approve">approval</span>
                            )}
                          </div>
                          <div className="mai-mind-action-title">{card.title}</div>
                          {card.subtitle && <div className="mai-mind-action-sub">{card.subtitle}</div>}

                          {(card.status === 'queued' || card.status === 'executing') && (
                            <textarea
                              className="mai-mind-edit-body"
                              value={card.editBody ?? card.approvalBody ?? ''}
                              onChange={(e) => updateAction(m.id, card.id, { editBody: e.target.value })}
                              rows={3}
                            />
                          )}

                          {(card.status === 'done' || card.status === 'queued' || card.status === 'error' || card.status === 'undone') && card.message && (
                            <div className={card.status === 'error' ? 'mai-cmd-confirm-err' : 'mai-cmd-confirm-ok'} style={{ marginTop: 10 }}>
                              {card.status === 'done' ? <Markdown text={card.message} /> : card.message}
                            </div>
                          )}

                          {card.status !== 'done-nav' && card.status !== 'done' && card.status !== 'undone' && (
                            <div className="mai-btn-row">
                              {(card.requiresConfirm || card.kind === 'mutation' || card.path.startsWith('mutation:')) ? (
                                <>
                                  {card.status !== 'queued' && card.status !== 'executing' && (
                                    <button
                                      type="button"
                                      className="mai-btn"
                                      disabled={card.status === 'submitting' || busy}
                                      onClick={() => void queueApproval(m.id, card, lastUserText)}
                                    >
                                      <FiCheck size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                                      {card.status === 'submitting' ? 'Queuing…' : 'Confirm'}
                                    </button>
                                  )}
                                  {(card.status === 'queued' || card.status === 'executing') && (
                                    <>
                                      <button
                                        type="button"
                                        className="mai-btn"
                                        disabled={card.status === 'executing' || busy}
                                        onClick={() => void executeOk(m.id, card)}
                                      >
                                        {card.status === 'executing' ? 'Running…' : 'Ok'}
                                      </button>
                                      <button
                                        type="button"
                                        className="mai-btn mai-btn-soft"
                                        onClick={() => updateAction(m.id, card.id, { status: 'queued' })}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="mai-btn mai-btn-ghost"
                                        onClick={async () => {
                                          if (card.approvalId && selectedSectorId) {
                                            await undoApproval(Number(selectedSectorId), card.approvalId);
                                          }
                                          updateAction(m.id, card.id, { status: 'undone', message: 'Undone.' });
                                        }}
                                      >
                                        Undo
                                      </button>
                                      <button
                                        type="button"
                                        className="mai-btn mai-btn-ghost"
                                        onClick={() => navigate('/central-inteligencia?tab=aprovacoes')}
                                      >
                                        Queue
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="mai-btn"
                                  onClick={() => handleActionClick(m.id, card)}
                                >
                                  {card.kind === 'nav' || card.path.startsWith('/') ? 'Open' : 'Run'}
                                  <FiArrowRight size={14} style={{ marginLeft: 6, verticalAlign: -2 }} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {busy && (
              <div className="mai-mind-row mai-mind-row-assistant">
                <div className="mai-mind-avatar" aria-hidden><FiCpu size={14} /></div>
                <div className="mai-mind-bubble mai-mind-bubble-assistant mai-mind-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="mai-mind-composer-wrap">
          {voiceHint && <div className="mai-mind-voice-hint">{voiceHint}</div>}
          <div className="mai-mind-composer">
            <button
              type="button"
              className={`mai-mind-mic ${recording ? 'mai-mind-mic-live' : ''}`}
              onClick={() => (recording ? stopRecording() : void startRecording())}
              disabled={busy && !recording}
              aria-label={recording ? 'Stop recording' : 'Record voice'}
              title="Record voice (STT → chat)"
            >
              {recording ? <FiSquare size={16} /> : <FiMic size={18} />}
            </button>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onComposerKey}
              placeholder="Ask Mends AI…"
              rows={1}
              disabled={busy || recording}
            />
            <button
              type="button"
              className="mai-mind-send"
              disabled={busy || recording || !input.trim()}
              onClick={() => void send(input)}
              aria-label="Send"
            >
              <FiSend size={18} />
            </button>
          </div>
          <div className="mai-mind-footnote">
            Voice → Whisper once (max 90s). Sends never broadcast — always confirm a single contact.
          </div>
        </footer>
      </div>
    </div>
  );
};

/** @deprecated — hotkey lives in CombinedMenu via useCommandPaletteHotkey */
export function useAiMindChatHotkey() {
  // no-op kept for import compatibility
}
